
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import type { Plan } from '@/api/plans';
import { apiClient } from '@/lib/api-client';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useGym } from '@/contexts/gym-context';
import { getClasses } from '@/api/classes';

const planSchema = z
  .object({
    name: z.string().min(2, {
      message: 'Name must be at least 2 characters.',
    }),
    price: z.coerce.number().min(0, {
      message: 'Price must be a positive number.',
    }),
    duration: z.coerce.number().min(1, {
      message: 'Duration must be at least 1.',
    }),
    durationType: z.enum(['days', 'months', 'years', 'fixed']),
    currency: z.string().min(1, {
      message: 'Currency is required.',
    }),
    daysPerMonth: z
      .union([z.coerce.number().min(1).max(31), z.null()])
      .optional(),
    isClass: z.boolean().optional(),
    classIds: z.array(z.string()).optional(),
  })
  .refine(
    (data) => {
      if (data.isClass) {
        return data.classIds && data.classIds.length > 0;
      }
      return true;
    },
    {
      message:
        "At least one class selection is required when 'Is Class Plan' is checked",
      path: ['classIds'],
    }
  );

type PlanFormValues = z.infer<typeof planSchema>;

interface EditPlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: Plan | null;
  onSuccess?: () => void;
}

export function EditPlanDialog({
  open,
  onOpenChange,
  plan,
  onSuccess,
}: EditPlanDialogProps) {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { gyms } = useGym();
  const [availableGyms, setAvailableGyms] = useState<
    { id: string; name: string; checked: boolean }[]
  >([]);
  const [classes, setClasses] = useState<
    { _id: string; name: string; label: string }[]
  >([]);
  const [isLoadingClasses, setIsLoadingClasses] = useState(false);
  const [selectedClassIds, setSelectedClassIds] = useState<string[]>([]);

  const form = useForm<PlanFormValues>({
    resolver: zodResolver(planSchema) as any,
    defaultValues: {
      name: '',
      price: 0,
      duration: 1,
      durationType: 'months',
      currency: 'USD',
      daysPerMonth: 3,
      isClass: false,
      classIds: [],
    },
  });

  const watchedDurationType = form.watch('durationType');
  const watchedIsClass = form.watch('isClass');

  const handleGymChange = (gymId: string, checked: boolean) => {
    setAvailableGyms((prev) =>
      prev.map((gym) => (gym.id === gymId ? { ...gym, checked } : gym))
    );
  };

  const handleClassChange = (classId: string, checked: boolean) => {
    let newSelectedIds: string[];
    if (checked) {
      newSelectedIds = [...selectedClassIds, classId];
    } else {
      newSelectedIds = selectedClassIds.filter((id) => id !== classId);
    }
    setSelectedClassIds(newSelectedIds);
    form.setValue('classIds', newSelectedIds);
  };

  useEffect(() => {
    const shouldLoadClasses = (watchedIsClass || plan?.isClass) && open;

    if (shouldLoadClasses) {
      setIsLoadingClasses(true);
      getClasses()
        .then((data) => {
          setClasses(data);
        })
        .catch((error) => {
          console.error('Error fetching classes:', error);
          toast({
            title: t('common.error'),
            description: 'Failed to load classes',
            variant: 'destructive',
          });
        })
        .finally(() => {
          setIsLoadingClasses(false);
        });
    }
  }, [watchedIsClass, plan?.isClass, open, t]);

  // Update form values when plan changes
  useEffect(() => {
    if (plan && gyms.length > 0) {
      const classIds = Array.isArray(plan.classId)
        ? plan.classId
        : plan.classId
        ? [plan.classId]
        : [];

      form.reset({
        name: plan.name,
        price: plan.price,
        duration: plan.duration,
        durationType: plan.durationType,
        currency: plan.currency,
        daysPerMonth: plan.daysPerMonth || null,
        isClass: plan.isClass || false,
        classIds: classIds,
      });

      setSelectedClassIds(classIds);

      setAvailableGyms(
        gyms.map((gym) => ({
          id: gym._id,
          name: gym.name,
          checked: plan.gyms ? plan.gyms.includes(gym._id) : false,
        }))
      );
    }
  }, [plan, gyms, form]);

  async function onSubmit(values: PlanFormValues) {
    if (!plan) return;

    const selectedGymIds = availableGyms
      .filter((gym) => gym.checked)
      .map((gym) => gym.id);

    if (selectedGymIds.length === 0) {
      toast({
        title: 'Error',
        description: t('plans.selectAtLeastOneGym'),
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await apiClient.patch(`/plans/${plan._id}`, {
        ...values,
        gyms: selectedGymIds,
        status: plan.status,
        isClass: values.isClass || false,
        classId: values.isClass ? values.classIds : undefined,
      });

      toast({
        title: t('plans.updateSuccess'),
        description: t('plans.planUpdatedSuccessfully'),
      });

      onOpenChange(false);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error updating plan:', error);
      toast({
        title: t('common.error'),
        description: t('plans.errorUpdatingPlan'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('plans.editPlan')}</DialogTitle>
          <DialogDescription>
            {t('plans.editPlanDescription')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('plans.name')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('plans.name')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('plans.price')}</FormLabel>
                    <FormControl>
                      <Input type="number" min="0" step="0.01" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('plans.currency')}</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={t('plans.selectCurrency')}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="USD" disabled>
                          USD ($)
                        </SelectItem>
                        <SelectItem value="EUR" disabled>
                          EUR (€)
                        </SelectItem>
                        <SelectItem value="GBP" disabled>
                          GBP (£)
                        </SelectItem>
                        <SelectItem value="ALL">ALL (L)</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="duration"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('plans.duration')}</FormLabel>
                    <FormControl>
                      <Input type="number" min="1" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="durationType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('plans.durationType')}</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={t('plans.selectDurationType')}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="days">{t('plans.days')}</SelectItem>
                        <SelectItem value="months">
                          {t('plans.months')}
                        </SelectItem>
                        <SelectItem value="years">
                          {t('plans.years')}
                        </SelectItem>
                        <SelectItem value="fixed">Fixed</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="daysPerMonth"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {watchedDurationType === 'fixed'
                      ? 'Number of sessions'
                      : 'Sessions per month'}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="1"
                      max="31"
                      {...field}
                      onChange={(e) => {
                        const value = e.target.value;
                        field.onChange(
                          value === '' ? null : Number.parseInt(value, 10)
                        );
                      }}
                      value={field.value === null ? '' : field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isClass"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>{t('plans.isClass', 'Is Class Plan')}</FormLabel>
                    <p className="text-sm text-muted-foreground">
                      {t(
                        'plans.isClassDescription',
                        'This plan is for a specific class'
                      )}
                    </p>
                  </div>
                </FormItem>
              )}
            />

            {watchedIsClass && (
              <FormField
                control={form.control}
                name="classIds"
                render={() => (
                  <FormItem>
                    <FormLabel>
                      {t('plans.selectClasses', 'Select Classes')}
                    </FormLabel>
                    {isLoadingClasses ? (
                      <div className="text-sm text-muted-foreground">
                        {t('common.loading', 'Loading...')}
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto">
                        {classes.map((gymClass) => (
                          <div
                            key={gymClass._id}
                            className="flex items-center space-x-2"
                          >
                            <Checkbox
                              id={`class-${gymClass._id}`}
                              checked={selectedClassIds.includes(gymClass._id)}
                              onCheckedChange={(checked) =>
                                handleClassChange(
                                  gymClass._id,
                                  checked as boolean
                                )
                              }
                            />
                            <Label
                              htmlFor={`class-${gymClass._id}`}
                              className="cursor-pointer text-sm"
                            >
                              {gymClass.name} - {gymClass.label}
                            </Label>
                          </div>
                        ))}
                        {classes.length === 0 && (
                          <div className="text-sm text-muted-foreground col-span-2">
                            {t(
                              'plans.noClassesAvailable',
                              'No classes available'
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="space-y-2">
              <Label>{t('plans.assignGyms')}</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {availableGyms.map((gym) => (
                  <div key={gym.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`gym-${gym.id}`}
                      checked={gym.checked}
                      onCheckedChange={(checked) =>
                        handleGymChange(gym.id, checked as boolean)
                      }
                    />
                    <Label htmlFor={`gym-${gym.id}`} className="cursor-pointer">
                      {gym.name}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? t('common.saving') : t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
